import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { ProductStatus } from "@medusajs/types";
import { Package, ShoppingCart } from "lucide-react";
import React from "react";
import type { OfferPriceState } from "@/utils/offerPriceState";
import { getProductDisplayTitle } from "@/utils/product";
import { PriceInfo } from "./PriceInfo";
import { ProductThumbnailImage } from "./ProductThumbnailImage";
import { useProductDetailDialog } from "./product-detail-dialog/ProductDetailDialogContext";
import { ProductRating } from "./ProductRating";

export interface ProductPreviewCardProps {
  /**
   * The product ID
   */
  productId: string;
  /**
   * Product's status
   */
  status: ProductStatus;
  variantId: string;
  offerId: string;
  title: string;
  brand: string;
  thumbnail: string | null;
  category: string;

  priceMinFormatted: string;
  priceMaxFormatted?: string;
  priceSavingFormatted?: string;
  sellerName: string;

  rating?: number;
  reviews?: number;
  tags?: string[];

  /**
   * Anything but `ready` replaces the price range with a loader or an error
   * (KAN-343): the range is the index's until pricing returns.
   */
  priceState?: OfferPriceState;
}

function ProductPreviewCard(props: ProductPreviewCardProps) {
  const { openDialog } = useProductDetailDialog();
  const disabled = props.status !== "published";

  const handleOpenDialog = () => {
    if (disabled) return;
    openDialog({
      offerId: props.offerId,
      sellerName: props.sellerName,
      productName: getProductDisplayTitle({
        title: props.title,
        brand: props.brand
      }),
      thumbnail: props.thumbnail,
      pricingProductId: props.productId
    });
  };

  return (
    <Card
      className="flex h-full cursor-pointer flex-col transition-shadow hover:shadow-lg"
      onClick={handleOpenDialog}
    >
      <CardContent className="flex flex-1 flex-col p-4">
        <div className="mb-4 flex aspect-square items-center justify-center rounded-lg bg-gray-100">
          <ProductThumbnailImage
            src={props.thumbnail}
            alt={props.title}
            className="h-full w-full object-cover"
            fallback={<Package className="h-12 w-12 text-gray-400" />}
          />
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <h3 className="text-sm font-semibold">
            {getProductDisplayTitle({ title: props.title, brand: props.brand })}
          </h3>
          <p className="mt-auto text-xs text-gray-500">{props.category}</p>
          <PriceInfo //
            priceMinFormatted={props.priceMinFormatted}
            priceMaxFormatted={props.priceMaxFormatted}
            priceSavingFormatted={props.priceSavingFormatted}
            priceState={props.priceState}
            enableAnimation
          />

          <div className="flex items-center justify-between">
            {props.rating ? (
              <ProductRating rating={props.rating} reviews={props.reviews} />
            ) : null}

            <p className="ml-auto text-xs text-gray-500">{props.sellerName}</p>
          </div>
          <Button
            size="sm"
            className="w-full"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog();
            }}
          >
            <ShoppingCart className="mr-2 h-3 w-3" />
            Add to Cart
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export const ProductPreviewCardLoader = () => {
  return (
    <Card>
      <CardContent className="p-4">
        <Skeleton className="mb-4 aspect-square w-full rounded-lg" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-5 w-12" />
          </div>
          <div className="flex items-center justify-between">
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-20" />
          </div>
          <Skeleton className="h-8 w-full rounded-md" />
        </div>
      </CardContent>
    </Card>
  );
};
ProductPreviewCardLoader.displayName = "ProductPreviewCardLoader";

export default React.memo(ProductPreviewCard);
